// Utility to check against .codecanvasignore patterns
import { isIgnoredPath } from "./CodeCanvasIgnore";

export type RepoTreeNodeType = "tree" | "blob";

// Minimal replica of the RepoItem shape used inside the Zustand store. We
// redefine it here to avoid creating a circular dependency by importing from
// the store file directly.
interface RepoItem {
  path: string;
  type: RepoTreeNodeType;
  fileName: string;
  contents?: string[];
}

interface RepoTreeNode {
  name: string;
  path: string;
  type: RepoTreeNodeType;
  children: Map<string, RepoTreeNode>;
}

/**
 * Lightweight in-memory representation of a repository directory structure.
 * Accepts the `repoData` object from the main Zustand store and converts the
 * flattened path map to a hierarchical tree.  The resulting structure can be
 * serialised via `printTree()` for consumption by LLMs or analytics jobs.
 */
export class RepoTree {
  root: RepoTreeNode;

  constructor(repoData: Record<string, RepoItem>) {
    // The root represents the repository root ("/")
    this.root = this.createNode("", "", "tree");
    this.buildTree(repoData);
  }

  /**
   * Serialises the repository tree into a *single-line* string that is more
   * compact than the original multi-line, indented representation.  The
   * algorithm is essentially a depth-first traversal that emits directory
   * names followed by their children in parentheses – similar to how a
   * concrete syntax tree might be printed.
   *
   *   Examples:
   *     foo(bar.txt,baz(qux.ts))  ➜  /foo/bar.txt  /foo/baz/qux.ts
   *
   * A few design considerations:
   *   • **Size**   The removal of newlines/indentation reduces the byte size
   *     roughly by ~30-40 % on large repositories.
   *   • **LLM-friendliness**   The output remains a plain UTF-8 string with a
   *     clear, deterministic grammar (<name>[ (<children>) ]*).  Most LLMs can
   *     parse it with a simple stack-based algorithm or even a regex.
   *   • **Completeness**   All nodes (files *and* directories) are still
   *     present; for directories the surrounding parentheses denote
   *     containment, while files are emitted as bare names.
   */
  printTree(
    excludeIgnoredFiles: boolean = false,
    humanReadable: boolean = false
  ): string {
    // Helper that returns the compact representation for a node (or an empty
    // string if the node is filtered out by `.codecanvasignore`).
    const serialize = (node: RepoTreeNode): string => {
      // Root node is emitted differently (see below), so we skip the name here.

      // Exclude ignored nodes when requested.
      if (excludeIgnoredFiles && node.path && isIgnoredPath(node.path)) {
        return "";
      }

      // Leaf node (file)
      if (node.type === "blob" || node.children.size === 0) {
        return node.name || ""; // root has empty name
      }

      // Directory – serialize children first (sorted for determinism)
      const childStrings = Array.from(node.children.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(serialize)
        .filter(Boolean); // Remove empty strings (ignored files / dirs)

      // If every child was filtered out we can drop this branch entirely.
      if (childStrings.length === 0) {
        return "";
      }

      // Root node gets special handling – we don't want to include an empty
      // label at the start ("(…)").  Instead we just return the joined
      // children wrapped in a single pair of parentheses so that the caller
      // can prepend "/" for clarity.
      if (node.name === "") {
        return `(${childStrings.join(",")})`;
      }

      // Regular directory
      return `${node.name}(${childStrings.join(",")})`;
    };

    const compact = `/${serialize(this.root)}`;

    // Fast-path: most callers only want the compact form.
    if (!humanReadable) {
      return compact;
    }

    // ---------------------------------------------------------------------
    // Human-readable reconstruction (debug helper)
    // ---------------------------------------------------------------------

    const lines: string[] = ["/"]; // start with the repo root
    let depth = 0;
    let buffer = "";

    const flushBufferAsFile = () => {
      if (buffer) {
        lines.push(`${"  ".repeat(depth)}${buffer}`);
        buffer = "";
      }
    };

    // Skip the first character ('/') which we already handled.
    for (let i = 1; i < compact.length; i++) {
      const ch = compact[i];

      switch (ch) {
        case "(":
          // Directory start – whatever is in buffer is the directory name.
          if (buffer) {
            lines.push(`${"  ".repeat(depth)}${buffer}/`);
            buffer = "";
          }
          depth += 1;
          break;
        case ",":
          // Separator between siblings. Flush current token (file).
          flushBufferAsFile();
          break;
        case ")":
          // Close of a directory. Flush any pending file token first.
          flushBufferAsFile();
          depth = Math.max(0, depth - 1);
          break;
        default:
          buffer += ch;
          break;
      }
    }

    // Flush anything left (shouldn't happen if input well-formed)
    flushBufferAsFile();

    return lines.join("\n");
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private buildTree(repoData: Record<string, RepoItem>) {
    Object.keys(repoData).forEach((fullPath) => {
      const repoItem = repoData[fullPath];
      const segments = fullPath.split("/").filter(Boolean);
      let currentNode = this.root;
      let currentPath = "";

      segments.forEach((segment, idx) => {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        const isLeaf = idx === segments.length - 1;
        const childType: RepoTreeNodeType = isLeaf
          ? repoItem.type // use actual type for the leaf
          : "tree";

        if (!currentNode.children.has(segment)) {
          const newNode = this.createNode(segment, currentPath, childType);
          currentNode.children.set(segment, newNode);
        }

        currentNode = currentNode.children.get(segment)!;
      });
    });
  }

  private createNode(
    name: string,
    path: string,
    type: RepoTreeNodeType
  ): RepoTreeNode {
    return {
      name,
      path,
      type,
      children: new Map<string, RepoTreeNode>(),
    };
  }
}
